import React, { useCallback, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { FctAviso } from '../fct/react';
import GcPagina from '../components/Layout/GcPagina';
import ListaPropuestas from '../components/TransmisionesAlta/ListaPropuestas';
import FormularioPropuesta, { type AccionAlta } from '../components/TransmisionesAlta/FormularioPropuesta';
import {
  errorAlta, leerAsesor, leerCatalogos, leerPropuestas, type AsesorAlta, type CatalogosAlta, type PropuestaAlta,
} from '../api/transmisionesAltaApi';
import { avisarGuardada } from '../embebido/puenteGdGo';
import { EVENTO_SIMULADO } from '../embebido/simuladorPuente';
import '../components/TransmisionesAlta/TransmisionesAlta.css';

interface TransmisionesAltaPageProps {
  /** Embebida en GdGO! (#326): sin el menú ni la cabecera de Gescom.aux. */
  embebida?: boolean;
}

type Vista = { tipo: 'lista' } | { tipo: 'nueva' } | { tipo: 'propuesta'; propuesta: PropuestaAlta };

const MENSAJES: Record<AccionAlta, string> = {
  guardada: 'Propuesta guardada.',
  vendida: 'Marcada como vendida: completa la fecha y los nuevos titulares.',
  notificada: 'Propuesta notificada.',
};

/**
 * El alta de propuestas de transmisión (#327, plan #323), para el móvil: la lista de propuestas del asesor del token y el
 * formulario para crear una o cambiarla. Sustituye a GCAuxC sobre la API de #324 y #325. Se usa con el menú de Gescom.aux
 * desde el navegador o, embebida, dentro de GdGO!.
 */
const TransmisionesAltaPage: React.FC<TransmisionesAltaPageProps> = ({ embebida = false }) => {
  const [asesor, setAsesor] = useState<AsesorAlta | null>(null);
  const [catalogos, setCatalogos] = useState<CatalogosAlta | null>(null);
  const [propuestas, setPropuestas] = useState<PropuestaAlta[]>([]);
  const [vista, setVista] = useState<Vista>({ tipo: 'lista' });
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [simulado, setSimulado] = useState<string | null>(null);

  // La simulación del puente (#326) anuncia lo que GdGO! recibiría.
  useEffect(() => {
    if (!embebida) return undefined;
    const anunciar = (e: Event) => setSimulado((e as CustomEvent<string>).detail);
    window.addEventListener(EVENTO_SIMULADO, anunciar);
    return () => window.removeEventListener(EVENTO_SIMULADO, anunciar);
  }, [embebida]);

  const recargar = useCallback(async () => setPropuestas(await leerPropuestas()), []);

  // El asesor, sus propuestas y los catálogos, a la vez. Sin ficha de empleado, la API responde 403 con su motivo.
  useEffect(() => {
    let vigente = true;
    Promise.all([leerAsesor(), leerPropuestas(), leerCatalogos()])
      .then(([a, p, c]) => {
        if (!vigente) return;
        setAsesor(a);
        setPropuestas(p);
        setCatalogos(c);
      })
      .catch((e) => vigente && setError(errorAlta(e).mensaje))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, []);

  // Tras vender una propuesta se sigue en ella, para completar los datos de la transmisión; lo demás vuelve a la lista.
  const alGuardar = async (propuesta: PropuestaAlta, accion: AccionAlta) => {
    // Embebida, tras guardar se avisa a GdGO! (#326), que decide si cierra la página.
    if (embebida && accion === 'guardada') avisarGuardada(propuesta.id);
    setAviso(MENSAJES[accion]);
    setVista(accion === 'vendida' ? { tipo: 'propuesta', propuesta } : { tipo: 'lista' });
    await recargar().catch((e) => setError(errorAlta(e).mensaje));
  };

  const alBorrar = async () => {
    setAviso('Propuesta borrada.');
    setVista({ tipo: 'lista' });
    await recargar().catch((e) => setError(errorAlta(e).mensaje));
  };

  const titulo = vista.tipo === 'lista' ? 'Mis propuestas' : vista.tipo === 'nueva' ? 'Nueva propuesta' : 'Propuesta';
  let contenido: React.ReactNode;
  if (cargando) {
    contenido = <Spin size="large" />;
  } else if (error && !catalogos) {
    contenido = <FctAviso tipo="error" titulo={error} />;
  } else if (vista.tipo === 'lista') {
    contenido = (
      <>
        {aviso && <FctAviso tipo="exito" titulo={aviso} />}
        {error && <FctAviso tipo="error" titulo={error} />}
        <ListaPropuestas
          propuestas={propuestas}
          onNueva={() => { setAviso(null); setVista({ tipo: 'nueva' }); }}
          onAbrir={(propuesta) => { setAviso(null); setVista({ tipo: 'propuesta', propuesta }); }}
        />
      </>
    );
  } else {
    const propuesta = vista.tipo === 'propuesta' ? vista.propuesta : undefined;
    contenido = (
      <>
        {aviso && <FctAviso tipo="exito" titulo={aviso} />}
        <FormularioPropuesta
          key={propuesta ? `${propuesta.id}-${propuesta.estadoId}` : 'nueva'}
          catalogos={catalogos!}
          propuesta={propuesta}
          asesorId={asesor?.id}
          onGuardada={alGuardar}
          onBorrada={alBorrar}
          onCancelar={() => { setAviso(null); setVista({ tipo: 'lista' }); }}
        />
      </>
    );
  }

  return embebida ? (
    <main className="gc-alta gc-alta--embebida">
      <h1 className="gc-alta__titulo">{titulo}</h1>
      {simulado && <FctAviso tipo="info" titulo={`Puente simulado: GdGO! recibiría ${simulado}`} />}
      {contenido}
    </main>
  ) : (
    <GcPagina seccion="Transmisiones" titulo={titulo} contexto={asesor?.nombre ?? undefined}>
      <div className="gc-alta">{contenido}</div>
    </GcPagina>
  );
};

export default TransmisionesAltaPage;
